import React, { useState, useEffect, useRef, useId } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Alert,
  CircularProgress,
  Dialog,
  DialogContent,
  AppBar,
  Toolbar,
  IconButton,
  FormControlLabel,
  Checkbox
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CloseIcon from '@mui/icons-material/Close';
import { formatPrice } from '../services/bookingService';

const EwaySecureFieldsPaymentForm = ({
  clinicId,
  serviceId,
  practitionerId,
  amount,
  patientEmail,
  patientName,
  appointmentDetails,
  onSuccess,
  onCancel,
  returnUrl,
  ewayConfig, // { publicApiKey, endpoint }
}) => {
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState(null);
  const [isFieldsReady, setIsFieldsReady] = useState(false);
  const [securedCardData, setSecuredCardData] = useState('');
  const [fieldValidation, setFieldValidation] = useState({
    name: false,
    card: false,
    expiry: false,
    cvn: false
  });
  const [isFirefox, setIsFirefox] = useState(false);
  const [dismissedFirefoxWarning, setDismissedFirefoxWarning] = useState(false);
  const [detailsConfirmed, setDetailsConfirmed] = useState(false);

  const nameFieldRef = useRef(null);
  const cardFieldRef = useRef(null);
  const expiryFieldRef = useRef(null);
  const cvnFieldRef = useRef(null);
  const fieldsInitialized = useRef(false);
  const securedCardDataRef = useRef('');

  // Generate unique IDs for this instance to prevent conflicts with multiple renders
  const instanceId = useId().replace(/:/g, '-');
  const fieldIds = {
    name: `eway-secure-field-name-${instanceId}`,
    card: `eway-secure-field-card-${instanceId}`,
    expiry: `eway-secure-field-expiry-${instanceId}`,
    cvn: `eway-secure-field-cvn-${instanceId}`
  };

  console.log('eWay Secure Fields Config received:', ewayConfig);

  // Detect Firefox browser
  useEffect(() => {
    const userAgent = navigator.userAgent.toLowerCase();
    const isFirefoxBrowser = userAgent.includes('firefox');
    setIsFirefox(isFirefoxBrowser);
    if (isFirefoxBrowser) {
      console.warn('Firefox detected - eWay Secure Fields may have compatibility issues due to Enhanced Tracking Protection');
    }
  }, []);

  // Load eWay script - use global flag to prevent multiple script loads
  useEffect(() => {
    // Skip if fields already initialized for this instance
    if (fieldsInitialized.current) return;

    // Check if eWay is already loaded globally
    if (window.eWAY) {
      console.log('eWAY already loaded globally, initializing fields for instance:', instanceId);
      initializeSecureFields();
      return;
    }

    // Check if script is already being loaded by another instance
    if (window.__ewayScriptLoading) {
      console.log('eWay script already loading, waiting...', instanceId);
      const checkInterval = setInterval(() => {
        if (window.eWAY) {
          clearInterval(checkInterval);
          console.log('eWAY now available, initializing fields for instance:', instanceId);
          initializeSecureFields();
        }
      }, 100);
      return () => clearInterval(checkInterval);
    }

    // Mark that we're loading the script
    window.__ewayScriptLoading = true;

    // Determine if we're in sandbox mode
    const isSandbox = ewayConfig?.sandbox === true ||
                      ewayConfig?.endpoint?.includes('sandbox');

    // eWay uses different domains for sandbox vs production
    // Sandbox: secure-au.sandbox.ewaypayments.com
    // Production: secure.ewaypayments.com
    const scriptUrl = isSandbox
      ? 'https://secure-au.sandbox.ewaypayments.com/scripts/eWAY.min.js'
      : 'https://secure.ewaypayments.com/scripts/eWAY.min.js';

    console.log('Loading eWay Secure Fields script from:', scriptUrl);
    console.log('eWay config:', { sandbox: ewayConfig?.sandbox, endpoint: ewayConfig?.endpoint });

    const script = document.createElement('script');
    script.src = scriptUrl;
    script.setAttribute('data-init', 'false');
    script.setAttribute('crossorigin', 'anonymous');
    script.async = true;
    script.onload = () => {
      console.log('eWAY script loaded');
      window.__ewayScriptLoading = false;
      initializeSecureFields();
    };
    script.onerror = () => {
      console.error('Failed to load eWAY script');
      window.__ewayScriptLoading = false;
      setError('Failed to load payment system. Please try again.');
    };
    document.head.appendChild(script);

    return () => {
      // Cleanup: remove iframes from field containers and reset state
      console.log('Cleaning up eWay Secure Fields for instance:', instanceId);
      fieldsInitialized.current = false;

      // Remove any iframes from our field containers
      Object.values(fieldIds).forEach(id => {
        const div = document.getElementById(id);
        if (div) {
          const iframes = div.querySelectorAll('iframe');
          iframes.forEach(iframe => iframe.remove());
          div.innerHTML = '';
        }
      });

      // Reset validation state
      setFieldValidation({ name: false, card: false, expiry: false, cvn: false });
      setSecuredCardData('');
      setIsFieldsReady(false);
      securedCardDataRef.current = '';
    };
  }, [ewayConfig?.sandbox, ewayConfig?.endpoint, ewayConfig?.publicApiKey, instanceId]);

  // Secure Fields callback function
  const secureFieldCallback = (event) => {
    console.log('eWay Secure Field callback:', event);

    // Clear any previous errors when field becomes valid
    if (event.fieldValid || event.valueIsValid) {
      setError(null);
    }

    // Update secured card data when available
    if (event.secureFieldCode) {
      console.log('Secured card data received');
      securedCardDataRef.current = event.secureFieldCode;
      setSecuredCardData(event.secureFieldCode);
    }

    // Update field validation status based on targetField
    // eWay uses different field names in callbacks
    const fieldMapping = {
      'name': 'name',
      'card': 'card',
      'expirytext': 'expiry',
      'cvn': 'cvn'
    };

    const fieldName = fieldMapping[event.targetField] || event.targetField;

    setFieldValidation(prev => {
      const updated = {
        ...prev,
        [fieldName]: event.valueIsValid === true
      };

      console.log('Field validation updated:', updated);

      // Check if all fields are valid
      const allFieldsValid = Object.values(updated).every(valid => valid === true);
      const hasSecuredData = !!event.secureFieldCode || !!securedCardDataRef.current;
      console.log('All fields valid:', allFieldsValid, 'Has secured data:', hasSecuredData);

      // Update fields ready state
      setIsFieldsReady(allFieldsValid && hasSecuredData);

      return updated;
    });

    // Show error only for actual validation failures (not empty fields during init)
    if (!event.fieldValid && event.errors && event.errors !== 'V6147') {
      console.error('Field validation error:', event.errors);
      setError(`Card validation error: ${event.errors}`);
    }
  };

  // Initialize eWay Secure Fields
  const initializeSecureFields = () => {
    // Skip if already initialized for this instance
    if (fieldsInitialized.current) {
      console.log('Secure Fields already initialized for instance:', instanceId);
      return;
    }

    console.log('Attempting to initialize eWay Secure Fields for instance:', instanceId);
    console.log('window.eWAY exists:', !!window.eWAY);
    console.log('publicApiKey:', ewayConfig?.publicApiKey);

    if (!window.eWAY || !ewayConfig?.publicApiKey) {
      console.error('eWAY not available or missing public API key');
      setError('Payment system configuration error. Please try again.');
      return;
    }

    // Wait for container divs to have proper dimensions before initializing
    const waitForDivsAndInit = (attempts = 0) => {
      const nameDiv = document.getElementById(fieldIds.name);
      const cardDiv = document.getElementById(fieldIds.card);
      const expiryDiv = document.getElementById(fieldIds.expiry);
      const cvnDiv = document.getElementById(fieldIds.cvn);

      console.log(`Attempt ${attempts + 1}: Checking div dimensions...`);
      console.log('DIV elements exist:', { name: !!nameDiv, card: !!cardDiv, expiry: !!expiryDiv, cvn: !!cvnDiv });

      if (nameDiv) {
        console.log('Name div dimensions:', nameDiv.offsetWidth, 'x', nameDiv.offsetHeight);
        console.log('Name div computed style:', window.getComputedStyle(nameDiv).display, window.getComputedStyle(nameDiv).width);

        // Debug: trace parent hierarchy to find what's collapsing
        if (attempts === 0) {
          console.log('=== PARENT HIERARCHY DEBUG ===');
          let parent = nameDiv.parentElement;
          let level = 1;
          while (parent && level <= 10) {
            const style = window.getComputedStyle(parent);
            console.log(`Level ${level}: <${parent.tagName}> class="${parent.className}" | display: ${style.display}, visibility: ${style.visibility}, width: ${style.width}, height: ${style.height}, offsetWidth: ${parent.offsetWidth}, offsetHeight: ${parent.offsetHeight}`);
            parent = parent.parentElement;
            level++;
          }
          console.log('=== END HIERARCHY ===');
        }
      }

      // Check if divs have dimensions
      const hasValidDimensions = nameDiv && cardDiv && expiryDiv && cvnDiv &&
        nameDiv.offsetWidth > 0 && nameDiv.offsetHeight > 0;

      if (!hasValidDimensions && attempts < 20) {
        console.log('Divs not ready, waiting 200ms...');
        setTimeout(() => waitForDivsAndInit(attempts + 1), 200);
        return;
      }

      if (!hasValidDimensions) {
        console.error('Divs never got proper dimensions after 20 attempts');
        // Try to initialize anyway
      }

      console.log('Divs ready, initializing Secure Fields...');
      doInitialize();
    };

    const doInitialize = () => {
      try {
        // Clear any existing iframes before initializing (prevents postMessage errors)
        Object.values(fieldIds).forEach(id => {
          const div = document.getElementById(id);
          if (div) {
            const existingIframes = div.querySelectorAll('iframe');
            if (existingIframes.length > 0) {
              console.log(`Clearing ${existingIframes.length} existing iframe(s) from ${id}`);
              existingIframes.forEach(iframe => iframe.remove());
            }
          }
        });

        const fieldStyles = `
          line-height: 1.5;
          height: 40px;
          border: 1px solid #E0E6ED;
          border-radius: 4px;
          color: #333;
          padding: 8px 12px;
          font-size: 16px;
          width: 100%;
          box-sizing: border-box;
        `;

        // Configure each field using unique IDs for this instance
        const nameFieldConfig = {
          publicApiKey: ewayConfig.publicApiKey,
          fieldDivId: fieldIds.name,
          fieldType: "name",
          styles: fieldStyles,
          autocomplete: "true"
        };

        const cardFieldConfig = {
          publicApiKey: ewayConfig.publicApiKey,
          fieldDivId: fieldIds.card,
          fieldType: "card",
          styles: fieldStyles,
          autocomplete: "true"
        };

        const expiryFieldConfig = {
          publicApiKey: ewayConfig.publicApiKey,
          fieldDivId: fieldIds.expiry,
          fieldType: "expirytext",
          styles: fieldStyles,
          autocomplete: "true"
        };

        const cvnFieldConfig = {
          publicApiKey: ewayConfig.publicApiKey,
          fieldDivId: fieldIds.cvn,
          fieldType: "cvn",
          styles: fieldStyles,
          autocomplete: "true"
        };

        // Initialize each field
        console.log('Calling setupSecureField for each field with IDs:', fieldIds);
        window.eWAY.setupSecureField(nameFieldConfig, secureFieldCallback);
        window.eWAY.setupSecureField(cardFieldConfig, secureFieldCallback);
        window.eWAY.setupSecureField(expiryFieldConfig, secureFieldCallback);
        window.eWAY.setupSecureField(cvnFieldConfig, secureFieldCallback);

        // Mark as initialized
        fieldsInitialized.current = true;
        console.log('eWay Secure Fields initialized for instance:', instanceId);

        // Debug: Check iframe dimensions after a delay
        setTimeout(() => {
          const allDivs = [fieldIds.name, fieldIds.card, fieldIds.expiry, fieldIds.cvn].map(id =>
            document.getElementById(id)
          );

          console.log('=== IFRAME DEBUG ===');
          allDivs.forEach((div, index) => {
            const names = ['name', 'card', 'expiry', 'cvn'];
            if (div) {
              const iframe = div.querySelector('iframe');
              console.log(`${names[index]} div: ${div.offsetWidth}x${div.offsetHeight}, iframe: ${iframe ? `${iframe.offsetWidth}x${iframe.offsetHeight}` : 'NO IFRAME'}`);
            }
          });
          console.log('====================');
        }, 1000);
      } catch (error) {
        console.error('Error initializing eWay Secure Fields:', error);
        setError('Failed to initialize payment form. Please try again.');
      }
    };

    // Start waiting for divs
    waitForDivsAndInit();
  };

  // Process payment
  const handlePayment = async () => {
    if (!securedCardData) {
      setError('Please complete all payment fields.');
      return;
    }

    try {
      setProcessing(true);
      setError(null);

      // Extract the clinic stub from the URL
      const pathParts = window.location.pathname.split('/');
      const clinicStub = pathParts[1];

      // Prepare payment data
      const paymentData = {
        clinic_id: clinicId,
        service_id: serviceId,
        practitioner_id: practitionerId,
        amount: amount,
        patient_email: patientEmail,
        patient_name: patientName,
        appointment_details: appointmentDetails,
        clinic_stub: clinicStub,
        return_url: returnUrl,
        secured_card_data: securedCardData,
        payment_method: 'eway_secure_fields'
      };

      console.log('Processing eWay Secure Fields payment...');

      // Call backend to process the payment
      const response = await fetch('/api/payments/eway/secure-fields', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(paymentData),
      });

      const result = await response.json();

      if (response.ok && result.success) {
        console.log('Payment successful:', result);
        console.log('Appointment created:', result.appointment_created, 'ID:', result.appointment_id);
        onSuccess({
          status: 'complete',
          transactionId: result.transactionId,
          accessCode: result.accessCode,
          processor: 'eway_secure_fields',
          appointment_created: result.appointment_created,
          appointment_id: result.appointment_id
        });
      } else {
        throw new Error(result.message || 'Payment failed');
      }
    } catch (error) {
      console.error('Payment error:', error);
      setError(error.message || 'Payment failed. Please try again.');
    } finally {
      setProcessing(false);
    }
  };

  return (
    <Dialog
      open={true}
      fullScreen
      onClose={onCancel}
      sx={{ zIndex: 1400 }}
    >
      <AppBar sx={{ position: 'relative' }}>
        <Toolbar>
          <IconButton
            edge="start"
            color="inherit"
            onClick={onCancel}
            aria-label="close"
            disabled={processing}
          >
            <CloseIcon />
          </IconButton>
          <Typography sx={{ ml: 2, flex: 1 }} variant="h6" component="div">
            Secure Payment
          </Typography>
        </Toolbar>
      </AppBar>
      <DialogContent sx={{ bgcolor: '#f5f5f5' }}>
    <Box sx={{ maxWidth: 600, mx: 'auto', p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={onCancel}
          sx={{ mr: 2 }}
          disabled={processing}
        >
          Back
        </Button>
        <Typography variant="h6">
          Payment - eWay Secure Fields
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {isFirefox && !dismissedFirefoxWarning && (
        <Alert
          severity="warning"
          sx={{ mb: 3 }}
          onClose={() => setDismissedFirefoxWarning(true)}
        >
          <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
            Firefox Browser Detected
          </Typography>
          <Typography variant="body2" sx={{ mb: 1 }}>
            Firefox's Enhanced Tracking Protection may interfere with secure payment processing.
            For the best experience, we recommend using <strong>Google Chrome</strong> or <strong>Safari</strong>.
          </Typography>
          <Typography variant="body2" sx={{ fontSize: '0.85rem', color: 'text.secondary' }}>
            If you continue with Firefox and encounter issues, try disabling Enhanced Tracking Protection
            for this site by clicking the shield icon in the address bar.
          </Typography>
        </Alert>
      )}

      <Paper variant="outlined" sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom>
          Enter Payment Details
        </Typography>
        
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Amount: {formatPrice(amount)} | Secure eWay Payment
        </Typography>

        {!ewayConfig?.publicApiKey ? (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Alert severity="error" sx={{ mb: 2 }}>
              eWay payment configuration is incomplete. Missing Public API Key.
            </Alert>
            <Button variant="outlined" onClick={onCancel}>
              Go Back
            </Button>
          </Box>
        ) : processing ? (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <CircularProgress />
            <Typography variant="body2" sx={{ mt: 2 }}>
              Processing payment...
            </Typography>
          </Box>
        ) : (
          <Box>
            {/* Scoped styles for eWay iframes using unique instance IDs */}
            {/* Hide duplicate labels that eWay creates below each field */}
            <style>{`
              #${fieldIds.name} iframe,
              #${fieldIds.card} iframe,
              #${fieldIds.expiry} iframe,
              #${fieldIds.cvn} iframe {
                width: 100% !important;
                height: 40px !important;
                min-height: 40px !important;
                border: none !important;
                display: block !important;
              }

              /* Hide any labels/text eWay creates outside the iframe */
              #${fieldIds.name} > span,
              #${fieldIds.name} > label,
              #${fieldIds.name} > div:not(:has(iframe)),
              #${fieldIds.card} > span,
              #${fieldIds.card} > label,
              #${fieldIds.card} > div:not(:has(iframe)),
              #${fieldIds.expiry} > span,
              #${fieldIds.expiry} > label,
              #${fieldIds.expiry} > div:not(:has(iframe)),
              #${fieldIds.cvn} > span,
              #${fieldIds.cvn} > label,
              #${fieldIds.cvn} > div:not(:has(iframe)) {
                display: none !important;
              }

              /* Also hide any siblings of the container divs that eWay might add */
              #${fieldIds.name} + span,
              #${fieldIds.name} + label,
              #${fieldIds.name} ~ span:not(svg),
              #${fieldIds.card} + span,
              #${fieldIds.card} + label,
              #${fieldIds.card} ~ span:not(svg),
              #${fieldIds.expiry} + span,
              #${fieldIds.expiry} + label,
              #${fieldIds.expiry} ~ span:not(svg),
              #${fieldIds.cvn} + span,
              #${fieldIds.cvn} + label,
              #${fieldIds.cvn} ~ span:not(svg) {
                display: none !important;
              }
            `}</style>

            <div style={{ marginBottom: '24px', width: '100%', maxWidth: '500px' }}>
              {/* Card Name */}
              <div style={{ marginBottom: '16px' }}>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                  Cardholder Name
                  {fieldValidation.name && <span style={{ color: '#4caf50', fontSize: '14px' }}>✓</span>}
                </Typography>
                <div
                  ref={nameFieldRef}
                  id={fieldIds.name}
                  style={{
                    display: 'block',
                    height: '44px',
                    width: '100%',
                    border: fieldValidation.name ? '1px solid #4caf50' : '1px solid #ccc',
                    borderRadius: '4px',
                    backgroundColor: '#fff',
                    boxSizing: 'border-box'
                  }}
                />
              </div>

              {/* Card Number */}
              <div style={{ marginBottom: '16px' }}>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                  Card Number
                  {fieldValidation.card && <span style={{ color: '#4caf50', fontSize: '14px' }}>✓</span>}
                </Typography>
                <div
                  ref={cardFieldRef}
                  id={fieldIds.card}
                  style={{
                    display: 'block',
                    height: '44px',
                    width: '100%',
                    border: fieldValidation.card ? '1px solid #4caf50' : '1px solid #ccc',
                    borderRadius: '4px',
                    backgroundColor: '#fff',
                    boxSizing: 'border-box'
                  }}
                />
              </div>

              {/* Expiry and CVN - side by side, responsive */}
              <div style={{ display: 'flex', gap: '16px', width: '100%', flexWrap: 'wrap' }}>
                <div style={{ flex: '1 1 120px', minWidth: '120px' }}>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                    Expiry Date
                    {fieldValidation.expiry && <span style={{ color: '#4caf50', fontSize: '14px' }}>✓</span>}
                  </Typography>
                  <div
                    ref={expiryFieldRef}
                    id={fieldIds.expiry}
                    style={{
                      display: 'block',
                      height: '44px',
                      width: '100%',
                      border: fieldValidation.expiry ? '1px solid #4caf50' : '1px solid #ccc',
                      borderRadius: '4px',
                      backgroundColor: '#fff',
                      boxSizing: 'border-box'
                    }}
                  />
                </div>
                <div style={{ flex: '1 1 120px', minWidth: '120px' }}>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                    CVV
                    {fieldValidation.cvn && <span style={{ color: '#4caf50', fontSize: '14px' }}>✓</span>}
                  </Typography>
                  <div
                    ref={cvnFieldRef}
                    id={fieldIds.cvn}
                    style={{
                      display: 'block',
                      height: '44px',
                      width: '100%',
                      border: fieldValidation.cvn ? '1px solid #4caf50' : '1px solid #ccc',
                      borderRadius: '4px',
                      backgroundColor: '#fff',
                      boxSizing: 'border-box'
                    }}
                  />
                </div>
              </div>
            </div>
            
            {/* Confirmation checkbox - triggers blur to validate fields */}
            <Box sx={{ mb: 2 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={detailsConfirmed}
                    onChange={(e) => {
                      // Blur any active element to trigger validation
                      if (document.activeElement) {
                        document.activeElement.blur();
                      }
                      setDetailsConfirmed(e.target.checked);
                    }}
                    color="primary"
                  />
                }
                label={
                  <Typography variant="body2">
                    I confirm my payment details are correct
                  </Typography>
                }
                sx={{
                  alignItems: 'flex-start',
                  '& .MuiCheckbox-root': { pt: 0 }
                }}
              />

              {/* Status indicator */}
              {detailsConfirmed && !isFieldsReady && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1, ml: 4 }}>
                  <CircularProgress size={14} />
                  <Typography variant="caption" color="text.secondary">
                    {Object.values(fieldValidation).every(v => v)
                      ? 'Securing card details...'
                      : 'Please complete all card fields'}
                  </Typography>
                </Box>
              )}
            </Box>

            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
              <Button
                variant="outlined"
                onClick={onCancel}
                disabled={processing}
              >
                Cancel
              </Button>

              <Button
                variant="contained"
                size="large"
                disabled={processing || !isFieldsReady || !detailsConfirmed}
                onClick={handlePayment}
                sx={{ minWidth: 120 }}
              >
                Pay {formatPrice(amount)}
                {processing && <CircularProgress size={20} sx={{ ml: 1 }} />}
              </Button>
            </Box>
          </Box>
        )}
      </Paper>
    </Box>
      </DialogContent>
    </Dialog>
  );
};

export default EwaySecureFieldsPaymentForm;